import React, { useState } from 'react';
import { Box, Flex, Text, SimpleGrid, Link } from '@chakra-ui/react';
import BrandLockup from '../ui/BrandLockup';
import { ChevronDown } from 'lucide-react';

const FooterAccordion = ({ title, links }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box borderBottomWidth="1px" borderColor="landing.border" display={{ base: 'block', md: 'none' }}>
      <Flex
        as="button"
        w="full"
        align="center"
        justify="space-between"
        py={4}
        bg="transparent"
        border="none"
        cursor="pointer"
        onClick={() => setOpen(!open)}
      >
        <Text fontSize="xs" fontWeight="900" color="text.main" letterSpacing="0.08em">
          {title.toUpperCase()}
        </Text>
        <Box
          color="text.muted"
          transition="transform 200ms"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <ChevronDown size={16} />
        </Box>
      </Flex>
      <Box
        overflow="hidden"
        maxH={open ? '300px' : '0'}
        transition="max-height 300ms ease"
      >
        <Flex direction="column" gap={3} pb={4}>
          {links.map((link, j) => (
            <Link
              key={j}
              href={link.href}
              fontSize="sm"
              color="text.muted"
              fontWeight="500"
              transition="all 150ms"
              _hover={{ color: 'brand.primary', paddingLeft: '4px' }}
            >
              {link.label}
            </Link>
          ))}
        </Flex>
      </Box>
    </Box>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    {
      title: 'Product',
      links: [
        { label: 'Features', href: '#features' },
        { label: 'Integrations', href: '#integrations' },
        { label: 'Pricing', href: '#pricing' },
        { label: 'FAQ', href: '#faq' },
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About', href: 'mailto:hello@datlynk.com' },
        { label: 'Contact', href: 'mailto:hello@datlynk.com' },
      ]
    },
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', href: '/privacy' },
        { label: 'Terms of Service', href: '/terms' },
      ]
    }
  ];

  return (
    <Box
      as="footer"
      bg="landing.surface"
      pt={20}
      pb={12}
      borderTopWidth="1px"
      borderColor="landing.border"
      fontFamily="body"
    >
      <Box maxW="1200px" mx="auto" px={{ base: 5, md: 8 }}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'stretch', md: 'flex-start' }}
          justify="space-between"
          gap={{ base: 0, md: 16 }}
          mb={{ base: 6, md: 16 }}
        >
          <Box flexShrink={0} maxW={{ base: '100%', md: '380px' }} mb={{ base: 0, md: 0 }}>
            {/* Brand + Social */}
            <Box mb={4}>
              <BrandLockup size={40} />
            </Box>
            <Text fontSize="md" color="text.secondary" mb={{ base: 0, md: 0 }} maxW="380px" lineHeight="1.8">
              DatLynk keeps Jira and ClickUp aligned with structure-aware sync today and is designed to expand across the rest of your workflow.
            </Text>
          </Box>

          {/* Desktop link columns */}
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing={12}
            display={{ base: 'none', md: 'grid' }}
            flex={1}
            minW={0}
          >
            {footerLinks.map((group, i) => (
              <Box key={i}>
                <Text fontSize="xs" fontWeight="900" color="text.main" letterSpacing="0.08em" mb={6}>
                  {group.title.toUpperCase()}
                </Text>
                <Flex direction="column" gap={4}>
                  {group.links.map((link, j) => (
                    <Link
                      key={j}
                      href={link.href}
                      fontSize="sm"
                      color="text.muted"
                      fontWeight="500"
                      transition="all 150ms"
                      _hover={{ color: 'brand.primary', paddingLeft: '4px' }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </Flex>
              </Box>
            ))}
          </SimpleGrid>
        </Flex>

        {/* Mobile accordion link groups */}
        <Box display={{ base: 'block', md: 'none' }} mb={10} borderTopWidth="1px" borderColor="landing.border">
          {footerLinks.map((group, i) => (
            <FooterAccordion key={i} title={group.title} links={group.links} />
          ))}
        </Box>

        {/* Bottom bar */}
        <Box pt={8} borderTopWidth="1px" borderColor="landing.border">
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'flex-start', md: 'center' }}
            gap={4}
          >
            <Text fontSize="xs" color="text.muted" fontWeight="500">
              © {currentYear} DatLynk Infrastructure. All rights reserved.
            </Text>
            <Flex gap={6}>
              <Link href="/terms" fontSize="xs" color="text.muted" fontWeight="500" _hover={{ color: 'brand.primary' }}>Terms of Service</Link>
              <Link href="/privacy" fontSize="xs" color="text.muted" fontWeight="500" _hover={{ color: 'brand.primary' }}>Privacy Policy</Link>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
